import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash, PencilSimple, UserCircle, X, MagnifyingGlass } from "@phosphor-icons/react";

/*
  NamePreferences · Iter 85.79
  ────────────────────────────
  Firm-wide preferred spellings for person names. YadidAI reads this
  list on every Organize run and prefers the canonical `preferred`
  string over any variant it sees in a transcript. Priority order:
    1. Canonical Contact display name (when contact_id is linked)
    2. This preference entry
    3. Explicit spelling in the source record
    4. AI inference (only when no authoritative spelling exists)

  Iter 85.79 · Contact linkage
  A row can now be linked to a canonical Contact via a relationship-
  aware selector on the Contact column. When linked, the Contact's
  display name becomes the authoritative spelling and this entry's
  `preferred` (if different) is treated as a variant/alias for
  drift-correction. When unlinked, the row remains a lightweight
  terminology memory (unchanged legacy behavior).
*/

// ── Compact firm-wide contact selector used inside each row ────────────
// Single-select, no inline creation (creation lives on Client/Contact pages).
function ContactSelector({ value, onChange, contacts, testIdPrefix }) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const wrapperRef = useRef(null);
  const linked = useMemo(
    () => (value ? contacts.find((c) => c.id === value) : null),
    [value, contacts],
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts.slice(0, 8);
    return contacts.filter((c) => {
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.title || "").toLowerCase().includes(q) ||
        (c.client_name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    }).slice(0, 8);
  }, [contacts, query]);

  const select = (c) => {
    onChange?.(c.id, c);
    setQuery("");
    setFocused(false);
  };
  const clear = () => {
    onChange?.(null, null);
    setQuery("");
  };

  if (linked) {
    return (
      <div className="inline-flex items-center gap-1.5 text-xs px-1.5 py-0.5 border border-primary/60 bg-primary/10 text-primary max-w-full"
           data-testid={`${testIdPrefix}-linked-chip`}>
        <UserCircle size={11} weight="bold" />
        <span className="truncate">{linked.name}</span>
        {linked.client_name && (
          <span className="text-primary/70 truncate">· {linked.client_name}</span>
        )}
        <button
          type="button"
          onClick={clear}
          data-testid={`${testIdPrefix}-clear`}
          aria-label="Unlink contact"
          className="text-primary/70 hover:text-destructive ml-1"
        >
          <X size={10} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={wrapperRef} data-testid={`${testIdPrefix}-selector`}>
      <div className={`flex items-center gap-1.5 border ${focused ? "border-primary" : "border-border"} bg-background px-2 py-1`}>
        <MagnifyingGlass size={10} className="text-muted-foreground shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 180)}
          placeholder="Link a contact…"
          className="flex-1 bg-transparent text-xs outline-none border-none px-0.5 min-w-[80px]"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      {focused && matches.length > 0 && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 border border-border bg-card shadow-lg max-h-60 overflow-y-auto min-w-[220px]"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(c)}
              data-testid={`${testIdPrefix}-option-${c.id}`}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-secondary/60 border-b border-border/40 last:border-b-0"
            >
              <div className="font-medium truncate">{c.name}</div>
              <div className="text-[0.625rem] font-mono text-muted-foreground/80 truncate">
                {[c.title, c.client_name].filter(Boolean).join(" · ") || c.email || "—"}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NamePreferences() {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [draft, setDraft] = useState({ preferred: "", variantsText: "", contact_id: null });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ preferred: "", variantsText: "", contact_id: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/firm/name-preferences");
      setRows(data?.entries || []);
    } catch (e) {
      // Silent — panel is optional.
    } finally { setLoading(false); }
  };
  const loadContacts = async () => {
    try {
      const { data } = await api.get("/contacts");
      setContacts((data?.contacts || []).map((c) => ({
        id: c.id, name: c.name, title: c.title, email: c.email,
        client_id: c.client_id, client_name: c.client_name,
      })));
    } catch (e) {
      setContacts([]);
    }
  };
  useEffect(() => { load(); loadContacts(); }, []);

  const parseVariants = (s) =>
    (s || "").split(/[,\n]/).map((v) => v.trim()).filter(Boolean);

  const add = async () => {
    const preferred = (draft.preferred || "").trim();
    if (!preferred) { toast.error("Enter a preferred spelling"); return; }
    setBusyId("__new__");
    try {
      const { data } = await api.post("/firm/name-preferences", {
        preferred,
        variants: parseVariants(draft.variantsText),
        contact_id: draft.contact_id || null,
      });
      setRows(data?.entries || []);
      setDraft({ preferred: "", variantsText: "", contact_id: null });
      toast.success(`Remembered '${preferred}' as the preferred spelling`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this preferred spelling?")) return;
    setBusyId(id);
    try {
      const { data } = await api.delete(`/firm/name-preferences/${id}`);
      setRows(data?.entries || []);
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  const beginEdit = (row) => {
    setEditingId(row.id);
    setEditForm({
      preferred: row.preferred || "",
      variantsText: (row.variants || []).join(", "),
      contact_id: row.contact_id || null,
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm({ preferred: "", variantsText: "", contact_id: null }); };
  const saveEdit = async (row) => {
    setBusyId(row.id);
    try {
      const { data } = await api.post("/firm/name-preferences", {
        id: row.id,
        preferred: editForm.preferred.trim(),
        variants: parseVariants(editForm.variantsText),
        contact_id: editForm.contact_id || null,
      });
      setRows(data?.entries || []);
      toast.success("Updated");
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusyId(null); }
  };

  const contactById = useMemo(() => {
    const m = {};
    for (const c of contacts) m[c.id] = c;
    return m;
  }, [contacts]);

  return (
    <section className="border border-border bg-card p-5" data-testid="name-preferences-panel">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">Firm terminology</div>
          <h3 className="font-display text-base font-semibold mt-1">Preferred name spellings</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            YadidAI prefers these spellings when it organizes meetings, drafts briefs, and generates memos. Link a
            row to a canonical Contact to make the Contact&apos;s name authoritative — the preferred and variant
            spellings then become drift-corrections that redirect back to the Contact.
          </p>
        </div>
      </div>

      <div className="grid gap-2 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.25fr_1.25fr_auto] gap-2 items-end">
          <div>
            <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Preferred spelling</div>
            <Input
              data-testid="name-pref-preferred"
              value={draft.preferred}
              onChange={(e) => setDraft({ ...draft, preferred: e.target.value })}
              placeholder="e.g. Stacy"
              className="rounded-none bg-secondary/40"
            />
          </div>
          <div>
            <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Variants (comma-separated)</div>
            <Input
              data-testid="name-pref-variants"
              value={draft.variantsText}
              onChange={(e) => setDraft({ ...draft, variantsText: e.target.value })}
              placeholder="Stacey, Staci, Stacie"
              className="rounded-none bg-secondary/40"
            />
          </div>
          <div>
            <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Contact (optional)</div>
            <ContactSelector
              value={draft.contact_id}
              onChange={(cid) => setDraft({ ...draft, contact_id: cid })}
              contacts={contacts}
              testIdPrefix="name-pref-new-contact"
            />
          </div>
          <Button
            data-testid="name-pref-add"
            onClick={add}
            disabled={busyId === "__new__"}
            className="rounded-none bg-primary text-primary-foreground h-10"
          >
            <Plus size={13} weight="bold" className="mr-1.5" /> {busyId === "__new__" ? "Adding…" : "Add"}
          </Button>
        </div>
      </div>

      {loading && <div className="text-xs text-muted-foreground">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-xs text-muted-foreground italic border border-dashed border-border px-3 py-4 text-center">
          No preferred spellings yet. Add one when YadidAI keeps mis-spelling a name.
        </div>
      )}
      {rows.length > 0 && (
        <div className="border border-border">
          <table className="w-full text-sm">
            <thead className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left px-3 py-2 font-normal">Preferred</th>
                <th className="text-left px-3 py-2 font-normal">Variants</th>
                <th className="text-left px-3 py-2 font-normal">Contact</th>
                <th className="text-right px-3 py-2 font-normal w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const editing = editingId === r.id;
                const linkedContact = r.contact_id ? contactById[r.contact_id] : null;
                return (
                  <tr key={r.id} className="border-b border-border/50" data-testid={`name-pref-row-${r.id}`}>
                    <td className="px-3 py-2 align-top">
                      {editing ? (
                        <Input
                          data-testid={`name-pref-${r.id}-preferred-input`}
                          value={editForm.preferred}
                          onChange={(e) => setEditForm({ ...editForm, preferred: e.target.value })}
                          className="rounded-none bg-background"
                        />
                      ) : (
                        <span className="font-medium">{r.preferred}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {editing ? (
                        <Input
                          data-testid={`name-pref-${r.id}-variants-input`}
                          value={editForm.variantsText}
                          onChange={(e) => setEditForm({ ...editForm, variantsText: e.target.value })}
                          className="rounded-none bg-background"
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {(r.variants || []).length > 0 ? r.variants.join(", ") : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-xs">
                      {editing ? (
                        <ContactSelector
                          value={editForm.contact_id}
                          onChange={(cid) => setEditForm({ ...editForm, contact_id: cid })}
                          contacts={contacts}
                          testIdPrefix={`name-pref-${r.id}-contact`}
                        />
                      ) : linkedContact ? (
                        <span
                          className="inline-flex items-center gap-1 text-primary"
                          data-testid={`name-pref-${r.id}-contact-linked`}
                          title={linkedContact.client_name || ""}
                        >
                          <UserCircle size={12} weight="bold" />
                          <span className="truncate max-w-[160px]">{linkedContact.name}</span>
                          {linkedContact.client_name && (
                            <span className="text-primary/60 truncate max-w-[120px]">· {linkedContact.client_name}</span>
                          )}
                        </span>
                      ) : r.contact_id ? (
                        <span
                          className="inline-flex items-center gap-1 text-muted-foreground"
                          data-testid={`name-pref-${r.id}-contact-linked`}
                        >
                          <UserCircle size={12} weight="bold" /> Linked
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editing ? (
                        <div className="inline-flex items-center gap-2 text-[0.625rem] font-mono uppercase tracking-widest">
                          <button
                            type="button"
                            data-testid={`name-pref-${r.id}-save`}
                            onClick={() => saveEdit(r)}
                            disabled={busyId === r.id}
                            className="text-brass hover:opacity-80 disabled:opacity-40"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            data-testid={`name-pref-${r.id}-cancel`}
                            onClick={cancelEdit}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => beginEdit(r)}
                            data-testid={`name-pref-${r.id}-edit`}
                            title="Edit"
                            className="text-muted-foreground hover:text-primary p-1"
                          >
                            <PencilSimple size={13} weight="bold" />
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(r.id)}
                            data-testid={`name-pref-${r.id}-delete`}
                            disabled={busyId === r.id}
                            title="Remove"
                            className="text-muted-foreground hover:text-destructive p-1 disabled:opacity-40"
                          >
                            <Trash size={13} weight="bold" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
